import { Heading } from '@/components/heading';
import { ModeIcon } from '@/components/mode-icon';
import { EmissionsStatement } from '@/components/modules/emissions/statement';
import { PortCode } from '@/components/port-code';
import {
  DISTANCE_UNITS,
  FREIGHTER_ROUTE,
  MODE_WATER,
  MOST_FREQUENT_ROUTE,
  QUICKEST_ROUTE,
  SHORTEST_ROUTE,
} from '@/lib/constants';
import { RouteSummaryModel } from '@/lib/models/route-summary/types';
import { LocationInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import {
  convertModeToPortType,
  convertModeToTransportType,
  formatDistance,
  formatDuration,
  formatFrequency,
  formatStops,
  formatTransfers,
} from '@/utils/format';
import { getPreferredIdentifier } from '@/utils/helpers';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { cn, tv } from '@/utils/styles';

import { PageRouteCarriers } from '../carriers';
import { PageRouteLines } from '../lines';

const getByLabelText = (label: string, mode: SegmentModeType): string => {
  switch (label) {
    case QUICKEST_ROUTE:
      return `Quickest ${convertModeToTransportType(mode)} route`;
    case SHORTEST_ROUTE:
      return `Closest ${convertModeToPortType(mode)}s`;
    case FREIGHTER_ROUTE:
      return `Cargo flights`;
    case MOST_FREQUENT_ROUTE:
      return `Most frequent`;
    default:
      return label;
  }
};

export interface PageRouteCardProps {
  from: LocationInterface;
  to: LocationInterface;
  route: RouteSummaryModel;
}

const PageRouteCard = ({ from, to, route }: PageRouteCardProps) => {
  const {
    routeSummary,
    routeOptions,
    origin: originPort,
    destination: destinationPort,
    originDistance,
    destinationDistance,
    label,
  } = route;
  const { frequency, durationSeconds, distanceKm, numTransfers, numStops, emissions, routeType } = routeSummary;

  // Calc totals and extra data
  const originPortCode = getPreferredIdentifier(originPort.identifiers);
  const destinationPortCode = getPreferredIdentifier(destinationPort.identifiers);
  const noPortCodes = !originPortCode && !destinationPortCode;

  const color = getTransportColor(routeType);

  const {
    card,
    inner,
    overview,
    locationName,
    stats,
    statsCell,
    primaryText,
    subText,
    contentWrapper,
    labelText,
    modeBar,
    modeBarRow,
  } = styles();

  return (
    <div className={card()} data-cy="page-routes-card">
      <h3 className={labelText()}>{label && getByLabelText(label, routeType)}</h3>
      <div className={inner()}>
        <Heading variant="h3" as="h4" className="leading-tight">
          {originPort?.name || from.name} to {destinationPort?.name || to.name}
        </Heading>
        <div className={overview()}>
          <div className={locationName()}>
            {originPortCode && (
              <PortCode port={originPort} mode={routeType} targetLocation={from} portDistance={originDistance} />
            )}
            <div className={cn({ 'flex-1': noPortCodes })}>
              <div className={modeBar()} style={{ '--row-background': color } as React.CSSProperties}>
                {[...Array(numTransfers + 1).keys()].map((segment) => (
                  <div key={segment} className={modeBarRow()}>
                    <ModeIcon mode={routeType} size="sm" />
                  </div>
                ))}
              </div>
            </div>
            {destinationPortCode && (
              <PortCode
                port={destinationPort}
                mode={routeType}
                targetLocation={to}
                portDistance={destinationDistance}
              />
            )}
          </div>
          <div className={stats()}>
            <dl className={statsCell()}>
              <dt className={primaryText()}>{formatDuration(durationSeconds)}</dt>
              <dd className={subText()}>{formatFrequency(frequency)}</dd>
            </dl>
            <dl className={statsCell()}>
              <dd className={primaryText()}>{formatDistance(distanceKm)}</dd>
              <dt className={subText()}>{formatDistance(distanceKm, DISTANCE_UNITS.MI)}</dt>
            </dl>
            <dl className={statsCell()}>
              <dd className={primaryText()}>{formatTransfers(numTransfers)}</dd>
              <dt className={subText()}>{formatStops(numStops)}</dt>
            </dl>
          </div>
        </div>
        {emissions && (
          <div className="mt-4">
            <EmissionsStatement label="Estimated emissions" emissions={emissions} />
          </div>
        )}
      </div>
      {routeOptions && (
        <div className={contentWrapper()}>
          {routeType === MODE_WATER ? (
            <PageRouteLines options={routeOptions} />
          ) : (
            <PageRouteCarriers options={routeOptions} mode={routeType} />
          )}
        </div>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    card: 'relative -ml-4 -mr-4 border-y-2 border-grey-200 bg-white lg:ml-0 lg:mr-0 lg:rounded-lg lg:border-2',
    inner: 'px-4 py-6 lg:p-8 lg:pb-6',
    overview: 'mt-4 grid gap-4 lg:grid-cols-2',
    locationName: 'flex items-center justify-between',
    stats: 'flex items-center justify-between leading-tight lg:justify-end',
    statsCell: 'py-2 lg:min-w-[100px] lg:border-l lg:border-grey-200 lg:px-4 lg:py-1',
    primaryText: 'm-0 block text-baseSm text-text-primary',
    subText: 'm-0 mt-1.5 overflow-hidden truncate whitespace-nowrap text-sm text-grey-500',
    labelText:
      'absolute -top-[10px] left-8 m-0 inline-block rounded-md bg-yellowDark px-2 py-1 text-sm font-semibold leading-none text-text-primary',
    contentWrapper: 'rounded-b-[5px] border-t border-grey-200 bg-grey-50 px-8 py-4 pb-2',
    modeBar:
      "relative z-3 mx-1 grid w-24 auto-cols-fr grid-flow-col rounded-[10px] bg-white after:absolute after:bottom-0 after:left-0 after:right-0 after:top-0 after:z-1 after:rounded-[10px] after:bg-[var(--row-background)] after:opacity-60 after:content-['']",
    modeBarRow: 'z-4 rounded-[10px]',
  },
});

export { PageRouteCard };
